import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  HostListener,
  ViewChild,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  Signal,
} from '@angular/core';
import { AnimationEvent } from '@angular/animations';
import { Subscription } from 'rxjs';

import { MatMenuTrigger } from '@angular/material/menu';
import { ScheduleApiService } from 'src/app/api/services/schedule-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import {
  DISPLAY_ACCESS,
  SCHEDULE_ACCESS,
} from 'src/app/auth/types/access-level';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { RvshareAppDetectionService } from 'src/app/screen-sharing/services/rvshare-app-detection.service';
import { type SidenavState } from '../app/app.component';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { StateService } from '@uirouter/core';
import {
  INTEGRATIONS_LIST_STATE,
  INTEGRATIONS_OPEN_STATE,
  INTEGRATIONS_STATE,
} from 'src/app/settings/services/integrations/integrations-company/integrations-company.routing';
import { CAP_ALERTS_REQUIRE_ROLE } from 'src/app/settings/services/integrations/models/integrations-company.catalog';

interface NavOption {
  id: string;
  label: string;
  route?: string;
  icon?: string;
  folders?: boolean;
  tooltip?: string;
  addon?: string;
  addonClass?: string;
  states?: string[];
  params?: Record<string, string>;
  clickHandler?: () => void;
  hasPopup?: 'dialog';
}

@Component({
  selector: 'sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  standalone: false,
})
export class SidebarComponent implements OnChanges, OnInit, OnDestroy {
  @Input() state: SidenavState = 'expand';
  @Input() parentAnimating = false;
  @Input() currentRoute: string = '';
  @Input() hideNavigation: boolean = false;
  @Input() isMobile: Signal<boolean>;
  @Output() companyMenuOpened = new EventEmitter<boolean>();
  @Output() toggleSidenav = new EventEmitter<void>();
  @ViewChild(MatMenuTrigger) companyMenuTrigger: MatMenuTrigger;

  animatingItems = false;
  animationState: string = 'expand';
  isCompanyMenuOpen = false;
  showMenu = true;
  titleLogo: 'rise-logo' | 'sidebar' = 'rise-logo';
  showSchedulesNav = false;

  private companyChangeSubscription: Subscription;
  private schedulesNavRequestId = 0;
  protected navOptions: Record<string, NavOption> = {};

  private refreshNavOptions() {
    this.navOptions = {
      presentations: {
        id: 'presentations-link',
        label: 'Presentations',
        route: 'apps.editor.list',
        icon: 'presentations',
        folders: true,
        states: [
          'apps.chat',
          'apps.editor.home',
          'apps.editor.list',
          'apps.editor.workspace.artboard',
          'apps.editor.workspace.htmleditor',
          'apps.editor.templates.edit',
          'apps.editor.create',
        ],
      },
      schedules: {
        id: 'schedules-link',
        label: 'Schedules',
        route: 'apps.schedules.list',
        icon: 'calendar',
        folders: true,
        states: [
          'apps.schedules.home',
          'apps.schedules.list',
          'apps.schedules.details',
          'apps.schedules.add',
        ],
      },
      displays: {
        id: 'displays-link',
        label: 'Displays',
        route: 'apps.displays.list',
        icon: 'display',
        folders: true,
        states: [
          'apps.displays.home',
          'apps.displays.list',
          'apps.displays.add',
          'apps.displays.activate',
          'apps.displays.details',
        ],
      },
      'screen-sharing': {
        id: 'screen-sharing-link',
        label: 'Screen Share',
        tooltip: 'Screen Share',
        route: 'apps.screen-sharing.moderator-join',
        icon: 'screen-share',
        states: [
          'apps.screen-sharing.moderator-join',
          'apps.screen-sharing.moderator-room',
        ],
      },
      'screen-sharing-upgrade': {
        id: 'screen-sharing-upgrade-button',
        label: 'Screen Share',
        icon: 'screen-share',
        addon: 'requires-upgrade',
        addonClass: 'small',
        tooltip: 'Upgrade to access Screen Sharing',
        clickHandler: this.upgradePlan.bind(this),
      },
      storage: {
        id: 'storage-link',
        label: 'Storage',
        route: 'apps.storage.home',
        icon: 'cloud-storage',
        states: ['apps.storage.home'],
      },
      settings: {
        id: 'settings-link',
        label: 'Settings',
        route: 'apps.settings.home',
        icon: 'settings',
        states: [
          'apps.settings.home',
          'apps.settings.company-licenses',
          'apps.settings.company-details',
          'apps.settings.sso-settings',
          'apps.settings.billing.home',
          'apps.settings.user-list',
        ],
      },
      'cap-alerts': {
        id: 'cap-alerts-link',
        label: 'CAP Alerts',
        route: INTEGRATIONS_OPEN_STATE,
        params: { integrationId: 'cap-alerts' },
        icon: 'cap-alerts',
        states: [INTEGRATIONS_OPEN_STATE],
        hasPopup: 'dialog',
      },
      'cap-alerts-upgrade': {
        id: 'cap-alerts-upgrade-button',
        label: 'CAP Alerts',
        icon: 'cap-alerts',
        addon: 'requires-upgrade',
        addonClass: 'small',
        tooltip: 'Upgrade to access CAP Alerts',
        hasPopup: 'dialog',
        clickHandler: this.upgradePlan.bind(
          this,
          FeaturesService.FEATURE_ALERTS,
        ),
      },
      integrations: {
        id: 'integrations-link',
        label: 'Integrations',
        route: INTEGRATIONS_LIST_STATE,
        icon: 'integrations',
        states: [
          INTEGRATIONS_STATE,
          INTEGRATIONS_LIST_STATE,
          INTEGRATIONS_OPEN_STATE,
        ],
      },
    };
  }

  constructor(
    protected companyStateService: CompanyStateService,
    protected userStateService: UserStateService,
    protected permissionService: PermissionService,
    protected featuresService: FeaturesService,
    protected rvshareAppDetectionService: RvshareAppDetectionService,
    private scheduleApiService: ScheduleApiService,
    private broadcasterService: BroadcasterService,
    private stateService: StateService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  isNavSelected(states?: string[], params?: Record<string, string>): boolean {
    if (!states || states.indexOf(this.currentRoute) === -1) {
      return false;
    }

    if (params) {
      return Object.entries(params).every(
        ([key, value]) => this.stateService.params?.[key] === value,
      );
    }

    return !Object.values(this.navOptions).some(
      (option) =>
        !!option.params &&
        !!option.states?.includes(this.currentRoute) &&
        Object.entries(option.params).every(
          ([key, value]) => this.stateService.params?.[key] === value,
        ),
    );
  }

  ngOnInit() {
    this.refreshSchedulesNavVisibility();

    this.companyChangeSubscription = this.broadcasterService.on(
      'risevision.company.selectedCompanyChanged',
      () => {
        this.refreshSchedulesNavVisibility();
        setTimeout(() => {
          this.showMenu = false;
          this.changeDetector.detectChanges();
          this.showMenu = true;
          this.changeDetector.detectChanges();
        });
      },
    );
  }

  canAccessDisplays(): boolean {
    return this.permissionService.hasAccess(DISPLAY_ACCESS.FULL_ACCESS);
  }

  canAccessScreenSharing(): boolean {
    return this.permissionService.hasAccess(
      DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR,
    );
  }

  canAccessSettings(): boolean {
    return (
      this.userStateService.hasPermission('sa ua') ||
      this.userStateService.canAccessBilling()
    );
  }

  canAccessCapAlerts(): boolean {
    return this.userStateService.hasPermission(CAP_ALERTS_REQUIRE_ROLE);
  }

  private refreshSchedulesNavVisibility(): void {
    const requestId = ++this.schedulesNavRequestId;

    if (this.permissionService.hasAccess(SCHEDULE_ACCESS.PLAYLIST_MANAGER)) {
      this.showSchedulesNav = true;
      return;
    }

    if (!this.userStateService.isAssignedPublisher()) {
      this.showSchedulesNav = false;
      return;
    }

    this.showSchedulesNav = false;

    this.scheduleApiService
      .list({ sortBy: 'name', count: 1 })
      .then((result) => {
        if (requestId !== this.schedulesNavRequestId) {
          return;
        }

        this.showSchedulesNav = !!(result.items && result.items.length);
        this.changeDetector.detectChanges();
      })
      .catch(() => {
        if (requestId !== this.schedulesNavRequestId) {
          return;
        }

        this.showSchedulesNav = false;
        this.changeDetector.detectChanges();
      });
  }

  ngOnDestroy() {
    this.schedulesNavRequestId++;

    if (this.companyChangeSubscription) {
      this.companyChangeSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['state']) {
      this.animationState = this.state;
      this.animatingItems = true;

      if (this.isCompanyMenuOpen) {
        this.updateMenuPosition();
      }
    }
    if (changes['currentRoute']) {
      if (!this.hideNavigation) {
        this.refreshNavOptions();
      }
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isCompanyMenuOpen) {
      this.updateMenuPosition();
    }
  }

  private updateMenuPosition(): void {
    const viewportWidth = window.innerWidth;
    let marginLeft = this.state === 'collapse' ? 50 : 250;

    // For smaller screens, ensure menu stays in viewport
    if (viewportWidth < 768) {
      marginLeft = Math.min(marginLeft, Math.max(10, viewportWidth - 450));
    }

    document.documentElement.style.setProperty(
      '--company-menu-margin-left',
      `${marginLeft}px`,
    );
  }

  handleMenuOpened() {
    this.isCompanyMenuOpen = true;
    this.updateMenuPosition();
    this.companyMenuOpened.emit(true);
  }

  handleMenuClosed() {
    this.isCompanyMenuOpen = false;
    this.companyMenuOpened.emit(false);
  }

  handleCloseMenu() {
    this.companyMenuTrigger.closeMenu();
  }

  upgradePlan(feature: string = FeaturesService.FEATURE_SCREEN_SHARING) {
    this.featuresService.showUpgradePlanModal(feature);
  }

  onSidebarAnimationStart(event: AnimationEvent): void {
    if (event.toState === 'expand' || event.toState === 'collapse') {
      this.animatingItems = true;
    }
  }

  onSidebarAnimationDone(): void {
    this.animatingItems = false;
  }

  onLogoClick(): void {
    if (this.state === 'expand' || this.isMobile()) {
      if (this.rvshareAppDetectionService.isScreenShareApp()) {
        this.stateService.go('apps.screen-sharing.home');
      } else {
        this.stateService.go('apps.home');
      }
    } else {
      this.toggleSidenav.emit();
    }
  }
}
